import classNames from 'clsx';
import { ReactComponent as IconClose } from 'img/icons/remove.svg';
import { find, propEq } from 'ramda';
import React, { PureComponent, ReactNode } from 'react';
import { Button } from 'reactApp/ui/Button/Button';
import { noop } from 'reactApp/utils/helpers';
import { Popup } from 'semantic-ui-react';

import styles from './Tip.css';

export interface TipData {
    id: string;
    onHideClick?(id: string): void;
    getTipContent?(isFolder: boolean): React.ReactElement | string;
}

interface IProps {
    show: boolean;
    showBubble: boolean;
    preventOverflow: boolean;
    position: 'top left' | 'top right' | 'bottom right' | 'bottom left' | 'right center' | 'left center' | 'top center' | 'bottom center';
    offset: number;
    text?: string | ReactNode;
    buttonText: string;
    icon: ReactNode | null;
    id: string;
    size?: 'mini' | 'tiny' | 'small' | 'large' | 'huge';
    className: string;
    hideTip(id?: string): void;
    tips?: TipData[];
}

interface IState {
    isOpen: boolean;
    show?: boolean;
}

export class Tip extends PureComponent<IProps, IState> {
    static defaultProps = {
        position: 'right center',
        size: 'large',
        preventOverflow: true,
        show: false,
        showBubble: true,
        className: '',
        buttonText: '',
        offset: 0,
        hideTip: noop,
        icon: null,
    };

    state = {
        isOpen: !!this.props.show,
        show: false,
    };

    timerId: number | null = null;

    static getDerivedStateFromProps(props) {
        return {
            show: !props.tips || find(propEq('id', props.id), props.tips),
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.closeTip);
    }

    componentWillUnmount() {
        this.clearTimer();
        window.removeEventListener('resize', this.closeTip);
    }

    closeTip = () => this.setState({ isOpen: false });

    handleOnHideClick = (e) => {
        e.stopPropagation();
        this.props.hideTip(this.props.id);
        this.closeTip();
    };

    clearTimer = () => {
        if (this.timerId) {
            clearTimeout(this.timerId);
        }
    };

    handleOpen = () => {
        this.clearTimer();

        this.setState({ isOpen: true });
    };

    handleClose = () => {
        this.clearTimer();

        this.timerId = window.setTimeout(() => this.closeTip(), 100000);
    };

    handleOnHover = () => this.clearTimer();

    handleOnMouseMove = (e) => {
        e.stopPropagation();
    };

    handleOnContextMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    handleDoubleClick = (e) => {
        e.stopPropagation();
    };

    render() {
        if (!this.state.show) {
            return null;
        }

        const { showBubble, preventOverflow, position, offset, size, text, buttonText, icon, className } = this.props;

        const popperModifiers = [];

        if (preventOverflow) {
            // @ts-ignore
            popperModifiers.push({ name: 'preventOverflow', enabled: true, options: {} });
        }

        return (
            <Popup
                trigger={
                    <div className={classNames({ [styles.root]: true, [styles.rootHidden]: !showBubble })}>
                        <div className={styles.dot} />
                    </div>
                }
                position={position}
                offset={[offset]}
                size={size}
                className={classNames({ [styles.tipPopup]: true, [className]: !!className })}
                hoverable
                flowing
                on="hover"
                onOpen={this.handleOpen}
                onClose={this.handleClose}
                open={this.state.isOpen}
                popperModifiers={popperModifiers}
                onMouseMove={this.handleOnMouseMove}
                onContextMenu={this.handleOnContextMenu}
            >
                <div onMouseEnter={this.handleOnHover} className={styles.content} onDoubleClick={this.handleDoubleClick}>
                    <div id="tipClose" className={styles.close} onClick={this.handleOnHideClick}>
                        <IconClose width={16} height={16} viewBox="0 0 16 16" />
                    </div>
                    {icon && <div className={styles.icon}>{icon}</div>}
                    <div>
                        {text}
                        {buttonText && (
                            <div className={styles.button}>
                                <Button onClick={this.handleOnHideClick} primary>
                                    {buttonText}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        );
    }
}
